import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { SearchTerminal } from "../../../../../http/patrol/types/terminals";
import { useTerminalsApi } from "../../../../../http/patrol/useTerminals";
import { PaginationQuery } from "../../../../../types";
import { formatBoolean } from "../../../../../utils";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateTerminalModalDialogButton from "./CreateTerminalModalDialogButton";
import EditTerminal from "./EditTerminal";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const Terminals: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const filters = useListFilters({ key: "terminals" });
  const aborter = useRef(new AbortController());
  const terminalsApi = useTerminalsApi();
  const { setActions, setTitle } = useSearchContext();

  const [itemToDelete, setItemToDelete] = useState<SearchTerminal>();
  const { accessesAction, accessesDialog } = useEntityAccesses<{ id: string; name?: string }>("patrol.terminal");
  const [itemToEdit, setItemToEdit] = useState<SearchTerminal>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "terminals");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = terminalsApi.useAdminTerminals(query, aborter.current.signal, patrolSystemId);

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await terminalsApi.updateAdminTerminalVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.terminals.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.terminals.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateTerminalModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<SearchTerminal>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">
              {i18n.translation.common.loop}/{i18n.translation.common.notLoop}
            </th>
            <th align="left">{i18n.translation.materials.plural}</th>
            {!patrolSystemId && <th align="left">{i18n.translation.systemTypes.plural}</th>}
            <th>{i18n.translation.common.visibility}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.item.id}`}>
                {item.item.code}
              </Link>
            </td>
            <td>
              {formatBoolean(i18n.translation, item.forLoop)}/{formatBoolean(i18n.translation, item.forNotLoop)}
            </td>
            <td>{item.supportMaterials?.join("; ")}</td>
            {!patrolSystemId && <td>{item.systems?.join("; ")}</td>}
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.item.id, value)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction({ id: item.item.id, name: item.item.name ?? item.item.code }),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.terminals.edit}>
          <EditTerminal onClose={() => setItemToEdit(undefined)} terminalId={itemToEdit?.item.id!} />
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.terminals.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                terminalsApi
                  .deleteAdminTerminal(itemToDelete.item.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.terminals.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default Terminals;
